/**
 * QuestionsAndFindingsSection — Merged Q&A + Findings display
 *
 * Renders each question with its answer, followed by findings linked to that question.
 * General findings (not linked to any question) appear at the bottom.
 * Includes findings generation header/controls.
 */

import { useState } from "react";
import { HelpCircle } from "lucide-react";
import { useFindingsByChecklistItem } from "@/packages/features-shared";
import type { UIChecklistItem, Id } from "@/packages/types";
import { QuestionCard } from "./QuestionCard";
import { FindingsSectionHeader } from "./findings/FindingsSectionHeader";
import { FindingsGenerationStatus } from "./findings/FindingsGenerationStatus";
import { DraftFindingCard } from "./findings/DraftFindingCard";
import { DraftBulkActions } from "./findings/DraftBulkActions";
import { FindingCard } from "./findings/FindingCard";
import { ManualFindingForm } from "./findings/ManualFindingForm";

// ─── Props ──────────────────────────────────────────────────────────────────

interface QuestionsAndFindingsSectionProps {
  item: UIChecklistItem;
  checklistItemId: Id<"checklistItems">;
  onSwitchToEvidence: () => void;
}

// ─── Inline Findings List for a specific filter ─────────────────────────────

type FindingDoc = NonNullable<
  ReturnType<typeof useFindingsByChecklistItem>
>[number];

interface InlineFindingsProps {
  findings: FindingDoc[];
  editingFindingId: string | null;
  onEdit: (id: string) => void;
  onEditSave: () => void;
  onEditCancel: () => void;
}

function InlineFindings({
  findings,
  editingFindingId,
  onEdit,
  onEditSave,
  onEditCancel,
}: InlineFindingsProps) {
  if (findings.length === 0) return null;

  const drafts = findings.filter((f) => f.status === "draft");
  const confirmed = findings.filter((f) => f.status !== "draft");

  return (
    <div className="space-y-2 ml-3 pl-3 border-l-2 border-border/50">
      {drafts.map((finding) => (
        <DraftFindingCard
          key={finding._id}
          finding={finding}
          isEditing={editingFindingId === finding._id}
          onEdit={() => onEdit(finding._id)}
          onEditSave={onEditSave}
          onEditCancel={onEditCancel}
        />
      ))}
      {confirmed.map((finding) => (
        <FindingCard
          key={finding._id}
          finding={finding}
          isEditing={editingFindingId === finding._id}
          onEdit={() => onEdit(finding._id)}
          onEditSave={onEditSave}
          onEditCancel={onEditCancel}
        />
      ))}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function QuestionsAndFindingsSection({
  item,
  checklistItemId,
  onSwitchToEvidence,
}: QuestionsAndFindingsSectionProps) {
  const findings = useFindingsByChecklistItem(checklistItemId);
  const [editingFindingId, setEditingFindingId] = useState<string | null>(null);

  const questions = item.questions ?? [];
  const answers = item.answers ?? [];
  const hasAnswers = answers.length > 0;

  const isGenerating = item.findingsGenerationStatus === "generating";
  const hasFailed = item.findingsGenerationStatus === "failed";

  const allFindings = findings ?? [];
  const drafts = allFindings.filter((f) => f.status === "draft");
  const generalFindings = allFindings.filter((f) => !f.questionOrder);

  const sortedQuestions = [...questions].sort((a, b) => a.order - b.order);
  const answerMap = new Map(answers.map((a) => [a.question.order, a]));

  function handleEdit(findingId: string) {
    setEditingFindingId(findingId);
  }

  function handleEditSave() {
    setEditingFindingId(null);
  }

  function handleEditCancel() {
    setEditingFindingId(null);
  }

  function getFindingsForQuestion(order: number) {
    return allFindings.filter((f) => f.questionOrder === order);
  }

  return (
    <div className="space-y-4">
      {/* Findings header with generation controls */}
      <FindingsSectionHeader
        checklistItemId={checklistItemId}
        hasAnswers={hasAnswers}
        hasDrafts={drafts.length > 0}
        isGenerating={isGenerating}
      />

      {isGenerating && <FindingsGenerationStatus state="loading" />}
      {hasFailed && !isGenerating && (
        <FindingsGenerationStatus
          state="error"
          checklistItemId={checklistItemId}
        />
      )}

      {/* Draft bulk actions (applies to all drafts) */}
      {!isGenerating && drafts.length > 0 && (
        <DraftBulkActions
          checklistItemId={checklistItemId}
          draftCount={drafts.length}
        />
      )}

      {/* Questions with their associated findings */}
      {sortedQuestions.length > 0 ? (
        <div className="space-y-3">
          {sortedQuestions.map((question) => {
            const questionFindings = getFindingsForQuestion(question.order);

            return (
              <div key={question.order} className="space-y-2">
                <QuestionCard
                  question={question}
                  existingAnswer={answerMap.get(question.order)}
                  checklistItemId={item.id}
                  onSwitchToEvidence={onSwitchToEvidence}
                  item={item}
                />

                {/* Findings for this question */}
                {!isGenerating && (
                  <InlineFindings
                    findings={questionFindings}
                    editingFindingId={editingFindingId}
                    onEdit={handleEdit}
                    onEditSave={handleEditSave}
                    onEditCancel={handleEditCancel}
                  />
                )}

                {/* Add finding button per question */}
                {!isGenerating && (
                  <div className="ml-3 pl-3 border-l-2 border-border/50">
                    <ManualFindingForm
                      checklistItemId={checklistItemId}
                      questionOrder={question.order}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center text-center py-4 gap-2">
          <HelpCircle size={24} className="text-muted/50" />
          <p className="text-[12px] text-muted">No questions generated yet</p>
        </div>
      )}

      {/* General findings (not tied to any question) */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-medium text-muted uppercase tracking-wider">
            General Findings
          </h4>
          {!isGenerating && (
            <ManualFindingForm checklistItemId={checklistItemId} />
          )}
        </div>

        {!isGenerating && (
          <InlineFindings
            findings={generalFindings}
            editingFindingId={editingFindingId}
            onEdit={handleEdit}
            onEditSave={handleEditSave}
            onEditCancel={handleEditCancel}
          />
        )}

        {!isGenerating && generalFindings.length === 0 && (
          <p className="text-[12px] text-muted py-2">No general findings.</p>
        )}
      </div>
    </div>
  );
}
